import { useCallback, useRef } from "react";
import { useTypedSelector } from "hooks";
import type { ResourceResponse, Scalar } from "types";
import { SetupIdentifier } from "consts/setups";
import { Filters, SamePersonIdentifierSource } from "consts/consumers";
import {
    postConsumer,
    getConsumers,
    updateOrCreateConsumerEmails
} from "features/consumers/api";
import { Consumer, ConsumerEmailEntity, ConsumersResponse } from "features/consumers/types";
import useActions from "features/general/useActions";
import { NotificationSeverity } from "features/general/types";
import { selectSetupsSlice } from "features/setups/selectors";
import { ManagementApiResponseFactory, processJsonResponse } from "util/api";
import { RequestQueryMapper } from "util/request-query-mapper";
import { TabIndex, TabProps } from "ui/organizms/Consumers/ConsumerDialogs/MultiTabDialog/types";
import type { AddDuplicateEmail } from "ui/forms/Consumer";
import { DuplicatedEmailRegistryEntry } from "../DuplicateEmails";

export default function useAddDuplicateEmail({
    getTabPayload,
    tabRegistry: [
        manageDuplicatesRegistry,
        updateManageDuplicatesRegistry
    ],
    dialogRequestProcessing: [, setDialogRequestProcessing]
}: TabProps<TabIndex.ManageDuplicates>) {
    const domains = useTypedSelector(selectSetupsSlice(SetupIdentifier.Mors));
    const { showNotication } = useActions();
    const addDuplicateEmailFormRef = useRef<ReturnType<typeof AddDuplicateEmail.useAddDuplicateEmailForm>>(null);

    const getFormValues = useCallback(() => {
        const {
            values: {
                email = '',
                samePersonIdentifierSource = ''
            } = {}
        } = addDuplicateEmailFormRef.current ?? {};

        return {
            email: email.trim().toLocaleLowerCase(),
            samePersonIdentifierSource: samePersonIdentifierSource.trim()
        };
    }, []);

    const resetForm = useCallback(() =>
        addDuplicateEmailFormRef.current?.resetForm()
        , []);

    const onReject = useCallback((error: Error) =>
        showNotication({
            message: error.message,
            severity: NotificationSeverity.Error
        }), [showNotication]);

    const handleAddDuplicateEmail = (consumerResourceResponse: ResourceResponse<Consumer>) => {
        if (!consumerResourceResponse.success) {
            throw new Error(consumerResourceResponse.message);
        }

        const { data: consumer } = consumerResourceResponse;
        const {
            email,
            samePersonIdentifierSource
        } = getFormValues();

        const { samePersonIdentifier, hashedEmail } = getTabPayload<Consumer>();

        return updateOrCreateConsumerEmails({
            coreId: consumer.coreId,
            samePersonIdentifier: samePersonIdentifier || hashedEmail,
            email,
            samePersonIdentifierSource: `${SamePersonIdentifierSource.Manual}: ${samePersonIdentifierSource}`,
        })
            .then(processJsonResponse)
            .then((consumerEmailResourseResponse: ResourceResponse<ConsumerEmailEntity>) => {
                if (!consumerEmailResourseResponse.success) {
                    throw new Error(consumerEmailResourseResponse.message);
                }

                updateManageDuplicatesRegistry([
                    new DuplicatedEmailRegistryEntry({
                        ...consumer,
                        email,
                        samePersonIdentifierSource
                    }),
                    ...manageDuplicatesRegistry,
                ]);

                resetForm();
            });
    };

    const handleCreateConsumer = (email: string) =>
        postConsumer({
            hashedEmail: email,
            MOR: domains.MOR
        })
            .then(processJsonResponse);

    const checkIfEmailAlreadyExists = (email: string) =>
        manageDuplicatesRegistry.some(({ email: existingEmail }) => existingEmail === email);

    const withInvariants = async <T extends Function>(fn: T, args: Array<Scalar>) => {
        const [email] = args;

        if (checkIfEmailAlreadyExists(String(email))) {
            throw new Error('Email already exists');
        }

        return fn(...args);
    };

    const handleGetConsumerByEmail = (email: string) =>
        getConsumers(
            RequestQueryMapper.from()
                .containsIn(Filters.email, email)
                .toString()
        )
            .then(processJsonResponse)
            .then((response: ConsumersResponse) => ManagementApiResponseFactory.make({
                ...response,
                data: response.data?.data.at(0)
            }));

    const onSubmit = () => {
        const { email } = getFormValues();
        setDialogRequestProcessing(true);

        return [
            null,
            withInvariants(handleGetConsumerByEmail, [email])
                .then((consumerResourceResponse: ResourceResponse<Consumer>) => (
                    consumerResourceResponse.data
                        ? Promise.resolve(consumerResourceResponse)
                        : handleCreateConsumer(email)
                ))
                .then(handleAddDuplicateEmail)
                .catch(onReject)
                .finally(() => setDialogRequestProcessing(false))
        ];
    };

    return {
        addDuplicateEmailFormRef,
        onSubmit
    };
};
